import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import type { Product } from '../types';

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const email = searchParams.get('email');
  const productId = searchParams.get('product') || 'tripwire'; // Default to tripwire

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [manualEmail, setManualEmail] = useState('');

  useEffect(() => {
    async function loadData() {
      setLoading(true);

      try {
        // Load product if productId provided
        if (productId) {
          const productRef = doc(db, 'products', productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            setProduct({ id: productSnap.id, ...productSnap.data() } as Product);
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [productId]);

  async function handleSendMagicLink() {
    const emailToUse = email || manualEmail.trim();
    if (!emailToUse) {
      alert('Please enter your email address');
      return;
    }

    setSendingLink(true);
    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/magic-link`,
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, emailToUse, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', emailToUse);
      setLinkSent(true);
    } catch (err: any) {
      console.error('Failed to send magic link:', err);
      alert(err.message || 'Failed to send sign-in link. Please try again.');
    } finally {
      setSendingLink(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-secondary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-2xl px-4">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-6xl mb-4">🎉</div>

          <h1 className="text-3xl font-bold text-primary mb-4">
            Thank You for Your Purchase!
          </h1>

          {product && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
              <p className="text-green-800 font-medium">
                {product.name} - R{product.priceZAR.toLocaleString()}
              </p>
            </div>
          )}

          {email && (
            <p className="text-lg text-secondary mb-6">
              Order confirmation sent to <strong>{email}</strong>
            </p>
          )}

          {user ? (
            // User is signed in - they can access downloads immediately
            <>
              <div className="border-t border-b py-6 my-6">
                <h2 className="text-xl font-semibold mb-4 text-green-600">✓ You're all set!</h2>
                <p className="text-secondary">
                  Your purchase is ready to download.
                </p>
              </div>

              <Link
                to="/account"
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-md font-medium hover:bg-blue-700 transition-colors mb-4"
              >
                Access Your Downloads
              </Link>
            </>
          ) : (
            // User is NOT signed in - need to create account or sign in
            <>
              <div className="border-t border-b py-6 my-6">
                <h2 className="text-xl font-semibold mb-4">Create Your Account</h2>
                <p className="text-secondary mb-4">
                  To access your downloads, we need to create an account for you.
                </p>

                {!linkSent ? (
                  <div className="space-y-4">
                    {email ? (
                      <p className="text-sm text-gray-600">
                        We'll send you a magic link to <strong>{email}</strong>.
                        Click the link to sign in instantly - no password needed!
                      </p>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 mb-2">
                          Enter your email address to receive a sign-in link:
                        </p>
                        <input
                          type="email"
                          value={manualEmail}
                          onChange={(e) => setManualEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                        />
                      </>
                    )}

                    <button
                      onClick={handleSendMagicLink}
                      disabled={sendingLink || (!email && !manualEmail.trim())}
                      className="bg-blue-600 text-white px-8 py-3 rounded-md font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {sendingLink ? 'Sending...' : 'Send Magic Link'}
                    </button>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <p className="text-blue-800 font-medium mb-2">📧 Check Your Email!</p>
                    <p className="text-sm text-blue-700">
                      We've sent a sign-in link to <strong>{email || manualEmail}</strong>.
                      Click the link in the email to access your downloads.
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      Didn't receive it? Check your spam folder or{' '}
                      <button
                        onClick={handleSendMagicLink}
                        className="underline hover:no-underline"
                      >
                        click here to resend
                      </button>
                    </p>
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-600 mb-4">
                <strong>OR</strong> if you already have an account:
              </p>

              <Link
                to="/account"
                className="inline-block border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-md font-medium hover:bg-blue-50 transition-colors"
              >
                Sign In to Existing Account
              </Link>
            </>
          )}

          <p className="text-sm text-secondary mt-6">
            Need help? Email{' '}
            <a href="mailto:support@dealvelocity.co.za" className="text-blue-600 hover:underline">
              support@dealvelocity.co.za
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
